// Invites (M7 slice 3; spec/dashboard.md section 12.4; spec/api-conventions.md section 21.3): Invite… on the organization's
// Members page (`invite-dialog`: email, organization role, optional per-game roles, optional access until, how long the link
// lives) → `POST /v1/orgs/{org}/invites`; the accept link is shown exactly once (`SecretOnce`) with "mailed" when the
// organization's SMTP sent it. The Pending invites section (`GET …/invites?status=pending`) lists rows `invite-<id>` with
// Resend (a new link, shown once; the old one dies) and Revoke (confirm). The dialog and the button are exported for the
// game's Settings › Members tab, which opens them in place with its game preselected (`game`): that game's role select
// comes first, then the organization role and the other games; `onInvited` lets the tab re-read its lists. Every role
// select names the role and describes it (`access/roles.ts`).
import { useEffect, useState, type FormEvent } from 'react';
import { ops } from '../../api/operations';
import type { Invite, InviteCreated } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { Button } from '../../ui/Button';
import { Chip } from '../../ui/Chip';
import { useConfirm } from '../../ui/ConfirmDialog';
import { Field } from '../../ui/Field';
import { TextInput } from '../../ui/inputs';
import { RoleHelp, RoleSelect } from '../../access/RoleSelect';
import { roleDescription, roleLabel } from '../../access/roles';
import { Modal } from '../../ui/Modal';
import { useToasts } from '../../ui/Toast';
import { DateTimeInput } from '../../ui/values';
import { queryCache, useQuery } from '../../workspace/cache';
import { asApiError, secondsToIso } from '../settings/keys';
import { SecretOnce } from '../settings/SecretOnce';
import './org.css';
import { LoadError, SkeletonRows } from '../../ui/PageStates';
import { EmptyState } from '../../ui/Banner';
import { Mail } from '../../ui/icons';

export const invitesKey = (org: string) => `org:${org}:invites:pending`;

/** The accept link of a create or a resend, shown once. */
export function InviteLinkDialog({ created, onClose }: { created: { title: string; result: InviteCreated } | null; onClose: () => void }) {
  const r = created?.result;
  return (
    <SecretOnce
      open={created !== null}
      title={created?.title ?? ''}
      label="Accept link"
      secret={r?.acceptUrl ?? ''}
      hint={r?.invite.hint}
      note={
        r ? (
          <p className="muted small invite-mailed" data-testid="invite-mailed">
            {r.mailed ? `Mailed to ${r.invite.email}. Hand the link over yourself only if the mail does not arrive.` : `Not mailed (the organization has no SMTP, or the send failed): hand this link to ${r.invite.email} yourself.`}
          </p>
        ) : undefined
      }
      onClose={onClose}
    />
  );
}

/** The game an invite is opened from: its role select comes first and starts at `role`. */
export interface InviteGame {
  slug: string;
  name: string;
  role?: string;
}

export function InviteButton({ org, missing, game, onInvited }: { org: string; missing: string | null; game?: InviteGame; onInvited?: () => void }) {
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState<{ title: string; result: InviteCreated } | null>(null);
  return (
    <>
      <Button icon={<Mail aria-hidden="true" />} onClick={() => setOpen(true)} missingPermission={missing}>
        Invite…
      </Button>
      <InviteDialog
        org={org}
        game={game}
        open={open}
        onClose={() => setOpen(false)}
        onCreated={(r) => {
          setOpen(false);
          setCreated({ title: `Invite for ${r.invite.email}`, result: r });
          queryCache.invalidate(invitesKey(org));
          onInvited?.();
        }}
      />
      <InviteLinkDialog created={created} onClose={() => setCreated(null)} />
    </>
  );
}

export function InviteDialog({ org, game, open, onClose, onCreated }: { org: string; game?: InviteGame; open: boolean; onClose: () => void; onCreated: (r: InviteCreated) => void }) {
  const { client } = useAuth();
  const gamesQ = useQuery(open ? `games:${org}` : null, () => client.games(org));
  const others = (gamesQ.data?.items ?? []).filter((g) => g.slug !== game?.slug);
  const initialGameRoles = (): Record<string, string> => (game ? { [game.slug]: game.role ?? 'viewer' } : {});
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [gameRoles, setGameRoles] = useState<Record<string, string>>(initialGameRoles);
  const [until, setUntil] = useState<number | null>(null);
  const [days, setDays] = useState('7');
  const [error, setError] = useState<ReturnType<typeof asApiError> | null>(null);
  const [busy, setBusy] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  useEffect(() => {
    if (open) {
      setEmail('');
      setRole('member');
      setGameRoles(initialGameRoles());
      setUntil(null);
      setDays('7');
      setError(null);
      setIdempotencyKey(crypto.randomUUID());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, game?.slug, game?.role]);
  const daysValid = /^\d+$/.test(days.trim()) && Number(days) >= 1 && Number(days) <= 30;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!daysValid) return;
    setBusy(true);
    setError(null);
    try {
      const picked = Object.entries(gameRoles)
        .filter(([, r]) => r)
        .map(([game, r]) => ({ game, role: r }));
      const body: { email: string; role: string; games?: { game: string; role: string }[]; expiresInDays?: number; accessExpiresAt?: string } = { email: email.trim(), role };
      if (picked.length) body.games = picked;
      if (Number(days) !== 7) body.expiresInDays = Number(days);
      const iso = secondsToIso(until);
      if (iso) body.accessExpiresAt = iso;
      onCreated(await client.data(ops.createInvite, { path: { org }, body, idempotencyKey }));
    } catch (err) {
      setError(asApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={game ? `Invite to ${game.name}` : 'Invite to the organization'} open={open} onClose={onClose} size="m">
      <form onSubmit={(e) => void submit(e)} data-testid="invite-dialog">
        <Field label="Email" required error={error?.fieldMessage('email')}>
          <TextInput type="email" aria-label="Email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="off" />
        </Field>
        {game && (
          <Field label={`Role in ${game.name}`} required help={roleDescription('game', gameRoles[game.slug])}>
            <RoleSelect kind="game" aria-label={`Role in ${game.name}`} value={gameRoles[game.slug] ?? ''} onChange={(e) => setGameRoles((cur) => ({ ...cur, [game.slug]: e.target.value }))} />
          </Field>
        )}
        <Field label="Organization role" required error={error?.fieldMessage('role')} help={roleDescription('org', role)}>
          <RoleSelect kind="org" aria-label="Organization role" value={role} onChange={(e) => setRole(e.target.value)} />
        </Field>
        {others.length > 0 && (
          <fieldset className="settings-scopes" aria-label={game ? 'Roles in other games' : 'Game roles'}>
            <legend>{game ? 'Roles in other games (optional)' : 'Game roles (optional)'}</legend>
            {others.map((g) => (
              <Field key={g.id} label={g.name}>
                <RoleSelect kind="game" emptyLabel="no game role" aria-label={`Role in ${g.name}`} value={gameRoles[g.slug] ?? ''} onChange={(e) => setGameRoles((cur) => ({ ...cur, [g.slug]: e.target.value }))} />
              </Field>
            ))}
          </fieldset>
        )}
        {error?.fieldMessage('games') && <div className="field-error">{error.fieldMessage('games')}</div>}
        <RoleHelp kind="game" summary="What can each game role do?" />
        <RoleHelp kind="org" summary="What can each organization role do?" />
        <Field label="Access until" help="Optional: the memberships the invite grants end then." error={error?.fieldMessage('accessExpiresAt')}>
          <DateTimeInput aria-label="Access until" allowEmpty value={until} onCommit={setUntil} />
        </Field>
        <Field label="Link valid for (days)" help="1 to 30." error={error?.fieldMessage('expiresInDays') ?? (daysValid ? undefined : 'A whole number of days from 1 to 30.')}>
          <TextInput type="number" min={1} max={30} aria-label="Link valid for (days)" value={days} onChange={(e) => setDays(e.target.value)} />
        </Field>
        {error && !error.details?.length && (
          <LoadError>{error.status === 409 ? `${error.message} Resend or revoke the pending invite below.` : error.message}</LoadError>
        )}
        <div className="modal-footer form-footer">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" busy={busy} disabled={!email.trim() || !daysValid}>
            Invite
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function PendingInvites({ org, missing, when }: { org: string; missing: string | null; when: (v: string | null | undefined) => string }) {
  const { client } = useAuth();
  const toasts = useToasts();
  const confirm = useConfirm();
  const key = invitesKey(org);
  const q = useQuery(missing ? null : key, () => client.all((cursor) => client.data(ops.listInvites, { path: { org }, query: { status: 'pending', limit: 200, cursor } })));
  const [busy, setBusy] = useState<string | null>(null);
  const [created, setCreated] = useState<{ title: string; result: InviteCreated } | null>(null);
  const reload = () => queryCache.invalidate(key);
  if (missing) return null;
  const rows = q.data ?? [];

  const resend = async (i: Invite) => {
    setBusy(i.id);
    try {
      const r = await client.data(ops.resendInvite, { path: { org, invite: i.id }, ifMatch: Number(i.version) });
      setCreated({ title: `New link for ${i.email}`, result: r });
    } catch (e) {
      const err = asApiError(e);
      toasts.error(err.message, { requestId: err.requestId });
    } finally {
      setBusy(null);
      reload();
    }
  };

  const revoke = async (i: Invite) => {
    if (!(await confirm({ title: `Revoke the invite for ${i.email}?`, body: 'Its link stops working at once.', confirmLabel: 'Revoke', tone: 'danger' }))) return;
    setBusy(i.id);
    try {
      await client.call(ops.revokeInvite, { path: { org, invite: i.id }, ifMatch: Number(i.version) });
      toasts.success(`The invite for ${i.email} was revoked.`);
    } catch (e) {
      const err = asApiError(e);
      toasts.error(err.message, { requestId: err.requestId });
    } finally {
      setBusy(null);
      reload();
    }
  };

  return (
    <div className="pane settings-block" data-testid="pending-invites">
      <div className="pane-head">
        <h2>Pending invites</h2>
        {rows.length > 0 && <span className="muted">{rows.length}</span>}
      </div>
      <div className="pane-body flush">
      {q.error ? (
        <div className="pane-body">
          <LoadError>The invites could not be read.</LoadError>
        </div>
      ) : q.loading && rows.length === 0 ? (
        <SkeletonRows rows={2} />
      ) : rows.length === 0 ? (
        <EmptyState icon={<Mail />} title="No pending invites." body="An invite waits here until its link is accepted, revoked or expires." />
      ) : (
        <table className="settings-table">
          <thead>
            <tr>
              <th scope="col">Email</th>
              <th scope="col">Role</th>
              <th scope="col">Games</th>
              <th scope="col">Link</th>
              <th scope="col">Link expires</th>
              <th scope="col">Access until</th>
              <th scope="col">Invited by</th>
              <th scope="col">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((i) => (
              <tr key={i.id} data-testid={`invite-${i.id}`}>
                <td>{i.email}</td>
                <td title={roleDescription('org', i.role)}>{roleLabel('org', i.role)}</td>
                <td>
                  <span className="settings-chips">
                    {i.games.map((g) => (
                      <Chip key={g.gameId} outline title={roleDescription('game', g.role)}>
                        {g.name}: {roleLabel('game', g.role)}
                      </Chip>
                    ))}
                  </span>
                </td>
                <td>
                  <code>
                    {i.prefix}…{i.hint}
                  </code>
                  {i.mailedAt && <span className="muted small"> · mailed</span>}
                </td>
                <td>{when(i.expiresAt)}</td>
                <td>{i.accessExpiresAt ? when(i.accessExpiresAt) : '—'}</td>
                <td>{i.invitedBy?.displayName ?? i.invitedBy?.email ?? '—'}</td>
                <td className="settings-actions">
                  <Button size="s" onClick={() => void resend(i)} busy={busy === i.id}>
                    Resend
                  </Button>
                  <Button size="s" variant="danger" onClick={() => void revoke(i)} disabled={busy === i.id}>
                    Revoke
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      </div>
      <InviteLinkDialog created={created} onClose={() => setCreated(null)} />
    </div>
  );
}
